import { useEffect, useState } from "react";
import { Plus, Trash2, Target, Clock, ShieldAlert, Ban, ListChecks } from "lucide-react";
import { api } from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";

const PHASES = [
    { value: "premarket", label: "Pre-market" },
    { value: "chart_review", label: "Chart review" },
    { value: "postmarket", label: "Post-market" },
];

export default function TradingRoutine() {
    const [instruments, setInstruments] = useState([]);
    const [rules, setRules] = useState([]);
    const [routine, setRoutine] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [reviewTimes, setReviewTimes] = useState([]);
    const [risks, setRisks] = useState([]);
    const [notrade, setNotrade] = useState([]);
    const [tab, setTab] = useState("routine");

    const load = async () => {
        const [i, r, ro, s, rt, rk, nt] = await Promise.all([
            api.get("/instruments"),
            api.get("/trading-rules"),
            api.get("/routine-items"),
            api.get("/sessions"),
            api.get("/chart-review-times"),
            api.get("/risk-limits"),
            api.get("/no-trade-conditions"),
        ]);
        setInstruments(i.data);
        setRules(r.data);
        setRoutine(ro.data.sort((a, b) => (a.order || 0) - (b.order || 0)));
        setSessions(s.data);
        setReviewTimes(rt.data);
        setRisks(rk.data);
        setNotrade(nt.data);
    };
    useEffect(() => {
        load();
    }, []);

    const create = async (path, body, setter, items) => {
        const { data } = await api.post(path, body);
        setter([...items, data]);
    };
    const remove = async (path, id, setter, items) => {
        await api.delete(`${path}/${id}`);
        setter(items.filter((x) => x.id !== id));
    };

    return (
        <div className="space-y-8" data-testid="trading-page">
            <PageHeader
                eyebrow="Rules of engagement"
                title="Trading Routine"
                subtitle="Configure the pieces of your plan. Edit anytime — this is your personal playbook."
            />

            <div className="flex gap-2 overflow-x-auto border-b border-white/5 pb-0" data-testid="trading-tabs">
                {[
                    { id: "routine", label: "Routine", icon: ListChecks },
                    { id: "instruments", label: "Instruments", icon: Target },
                    { id: "sessions", label: "Sessions", icon: Clock },
                    { id: "rules", label: "Rules & risk", icon: ShieldAlert },
                    { id: "notrade", label: "No-trade", icon: Ban },
                ].map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        data-testid={`tab-${t.id}`}
                        className={`px-4 py-2.5 text-sm flex items-center gap-2 border-b-2 transition-colors ${
                            tab === t.id ? "border-[#D4AF37] text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"
                        }`}
                    >
                        <t.icon className="w-4 h-4" strokeWidth={1.5} />
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "routine" && (
                <section className="grid lg:grid-cols-3 gap-6">
                    {PHASES.map((p) => (
                        <RoutineCol
                            key={p.value}
                            phase={p}
                            items={routine.filter((r) => r.phase === p.value)}
                            onAdd={(title) =>
                                create(
                                    "/routine-items",
                                    { phase: p.value, title, order: routine.length },
                                    setRoutine,
                                    routine
                                )
                            }
                            onDelete={(id) => remove("/routine-items", id, setRoutine, routine)}
                        />
                    ))}
                    <div className="twp-card p-6 lg:col-span-3" data-testid="chart-review-times-card">
                        <div className="caption">Chart review times (America/New_York)</div>
                        <h3 className="font-serif text-xl mt-1">Scheduled reviews</h3>
                        <TimeList
                            items={reviewTimes}
                            onAdd={(label, time) =>
                                create("/chart-review-times", { label, time_hhmm: time }, setReviewTimes, reviewTimes)
                            }
                            onDelete={(id) => remove("/chart-review-times", id, setReviewTimes, reviewTimes)}
                        />
                    </div>
                </section>
            )}

            {tab === "instruments" && (
                <section className="twp-card p-6">
                    <h3 className="font-serif text-xl">Futures instruments</h3>
                    <p className="text-sm text-zinc-500 mt-1">Add the symbols you follow (e.g. ES, NQ, CL, GC).</p>
                    <InstrumentForm onAdd={(d) => create("/instruments", d, setInstruments, instruments)} />
                    <div className="mt-5 divide-y divide-white/5">
                        {instruments.map((i) => (
                            <div key={i.id} className="py-3 flex items-center gap-4" data-testid={`inst-${i.symbol}`}>
                                <div className="font-mono text-[#D4AF37] text-sm w-20">{i.symbol}</div>
                                <div className="flex-1">
                                    <div className="text-sm">{i.name}</div>
                                    <div className="text-xs text-zinc-500">
                                        tick {i.tick_size || "—"} · pt ${i.point_value || "—"}
                                    </div>
                                </div>
                                <button
                                    onClick={() => remove("/instruments", i.id, setInstruments, instruments)}
                                    className="text-zinc-500 hover:text-[#B95050] p-1.5"
                                    data-testid={`del-inst-${i.symbol}`}
                                >
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </div>
                        ))}
                        {instruments.length === 0 && <div className="py-6 text-sm text-zinc-500">None yet.</div>}
                    </div>
                </section>
            )}

            {tab === "sessions" && (
                <section className="twp-card p-6">
                    <h3 className="font-serif text-xl">Trading sessions (ET)</h3>
                    <SessionForm onAdd={(d) => create("/sessions", d, setSessions, sessions)} />
                    <div className="mt-5 divide-y divide-white/5">
                        {sessions.map((s) => (
                            <div key={s.id} className="py-3 flex items-center gap-4" data-testid={`sess-${s.id}`}>
                                <div className="flex-1">
                                    <div className="text-sm">{s.name}</div>
                                    <div className="text-xs text-zinc-500 font-mono">
                                        {s.start_hhmm} – {s.end_hhmm}
                                    </div>
                                </div>
                                <button
                                    onClick={() => remove("/sessions", s.id, setSessions, sessions)}
                                    className="text-zinc-500 hover:text-[#B95050] p-1.5"
                                >
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {tab === "rules" && (
                <section className="grid md:grid-cols-2 gap-6">
                    <div className="twp-card p-6">
                        <h3 className="font-serif text-xl">Trading rules</h3>
                        <SimpleAdd placeholder="e.g. Never add to a loser" onAdd={(t) => create("/trading-rules", { text: t }, setRules, rules)} />
                        <ul className="mt-4 space-y-2">
                            {rules.map((r) => (
                                <li key={r.id} className="flex items-start gap-3 py-2 border-b border-white/5" data-testid={`rule-${r.id}`}>
                                    <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2" />
                                    <span className="text-sm flex-1">{r.text}</span>
                                    <button onClick={() => remove("/trading-rules", r.id, setRules, rules)} className="text-zinc-500 hover:text-[#B95050]">
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="twp-card p-6">
                        <h3 className="font-serif text-xl">Risk limits</h3>
                        <RiskForm onAdd={(d) => create("/risk-limits", d, setRisks, risks)} />
                        <ul className="mt-4 space-y-2">
                            {risks.map((r) => (
                                <li key={r.id} className="flex items-start gap-3 py-2 border-b border-white/5">
                                    <div className="flex-1">
                                        <div className="text-sm">{r.name}</div>
                                        <div className="text-xs text-zinc-500">{r.value} {r.notes && `· ${r.notes}`}</div>
                                    </div>
                                    <button onClick={() => remove("/risk-limits", r.id, setRisks, risks)} className="text-zinc-500 hover:text-[#B95050]">
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {tab === "notrade" && (
                <section className="twp-card p-6">
                    <h3 className="font-serif text-xl">No-trade conditions</h3>
                    <p className="text-sm text-zinc-500 mt-1">Clearly list when you will NOT trade (poor sleep, major news, emotional state).</p>
                    <SimpleAdd placeholder="e.g. Less than 6 hours of sleep" onAdd={(t) => create("/no-trade-conditions", { condition: t }, setNotrade, notrade)} />
                    <ul className="mt-4 space-y-2">
                        {notrade.map((n) => (
                            <li key={n.id} className="flex items-start gap-3 py-2 border-b border-white/5">
                                <Ban className="w-4 h-4 text-[#B95050] mt-0.5" strokeWidth={1.5} />
                                <span className="text-sm flex-1">{n.condition}</span>
                                <button onClick={() => remove("/no-trade-conditions", n.id, setNotrade, notrade)} className="text-zinc-500 hover:text-[#B95050]">
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <ChartObservationSection instruments={instruments} />
        </div>
    );
}

function RoutineCol({ phase, items, onAdd, onDelete }) {
    const [title, setTitle] = useState("");
    return (
        <div className="twp-card p-5" data-testid={`routine-col-${phase.value}`}>
            <div className="caption">{phase.label}</div>
            <h3 className="font-serif text-lg mt-1">Checklist</h3>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    if (!title.trim()) return;
                    onAdd(title.trim());
                    setTitle("");
                }}
                className="mt-4 flex gap-2"
            >
                <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Add item"
                    data-testid={`routine-input-${phase.value}`}
                    className="flex-1 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#D4AF37]/60"
                />
                <button className="bg-[#D4AF37] text-[#0E0E10] px-3 rounded-md hover:bg-[#B3922E]" data-testid={`routine-add-${phase.value}`}>
                    <Plus className="w-4 h-4" />
                </button>
            </form>
            <ul className="mt-4 space-y-1.5">
                {items.map((it) => (
                    <li key={it.id} className="flex items-center justify-between gap-2 group text-sm px-2 py-1 rounded hover:bg-white/5">
                        <span className="truncate">{it.title}</span>
                        <button onClick={() => onDelete(it.id)} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-[#B95050]">
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function TimeList({ items, onAdd, onDelete }) {
    const [label, setLabel] = useState("");
    const [time, setTime] = useState("");
    return (
        <>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    if (!label || !time) return;
                    onAdd(label, time);
                    setLabel("");
                    setTime("");
                }}
                className="mt-4 flex gap-2 flex-wrap"
            >
                <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. London open)" className="flex-1 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="crt-label" />
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="crt-time" />
                <button className="bg-[#D4AF37] text-[#0E0E10] px-3 rounded-md hover:bg-[#B3922E] text-sm" data-testid="crt-add"><Plus className="w-4 h-4" /></button>
            </form>
            <div className="mt-4 flex flex-wrap gap-2">
                {items.map((t) => (
                    <div key={t.id} className="px-3 py-2 bg-[#0e0e10] rounded-md border border-white/10 flex items-center gap-3 text-sm">
                        <span className="font-mono gold">{t.time_hhmm}</span>
                        <span>{t.label}</span>
                        <button onClick={() => onDelete(t.id)} className="text-zinc-500 hover:text-[#B95050]"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                ))}
            </div>
        </>
    );
}

function InstrumentForm({ onAdd }) {
    const [v, setV] = useState({ symbol: "", name: "", tick_size: "", point_value: "" });
    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                if (!v.symbol) return;
                onAdd({
                    symbol: v.symbol.toUpperCase(),
                    name: v.name,
                    tick_size: parseFloat(v.tick_size) || undefined,
                    point_value: parseFloat(v.point_value) || undefined,
                });
                setV({ symbol: "", name: "", tick_size: "", point_value: "" });
            }}
            className="mt-4 grid md:grid-cols-5 gap-2"
        >
            <input placeholder="Symbol" value={v.symbol} onChange={(e) => setV({ ...v, symbol: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm uppercase" data-testid="inst-symbol" />
            <input placeholder="Name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} className="md:col-span-2 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="inst-name" />
            <input placeholder="Tick" value={v.tick_size} onChange={(e) => setV({ ...v, tick_size: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
            <div className="flex gap-2">
                <input placeholder="$/pt" value={v.point_value} onChange={(e) => setV({ ...v, point_value: e.target.value })} className="flex-1 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                <button className="bg-[#D4AF37] text-[#0E0E10] px-3 rounded-md hover:bg-[#B3922E]" data-testid="inst-add"><Plus className="w-4 h-4" /></button>
            </div>
        </form>
    );
}

function SessionForm({ onAdd }) {
    const [v, setV] = useState({ name: "", start_hhmm: "", end_hhmm: "" });
    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                if (!v.name || !v.start_hhmm || !v.end_hhmm) return;
                onAdd(v);
                setV({ name: "", start_hhmm: "", end_hhmm: "" });
            }}
            className="mt-4 grid md:grid-cols-4 gap-2"
        >
            <input placeholder="Session name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} className="md:col-span-2 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="sess-name" />
            <input type="time" value={v.start_hhmm} onChange={(e) => setV({ ...v, start_hhmm: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="sess-start" />
            <div className="flex gap-2">
                <input type="time" value={v.end_hhmm} onChange={(e) => setV({ ...v, end_hhmm: e.target.value })} className="flex-1 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="sess-end" />
                <button className="bg-[#D4AF37] text-[#0E0E10] px-3 rounded-md hover:bg-[#B3922E]" data-testid="sess-add"><Plus className="w-4 h-4" /></button>
            </div>
        </form>
    );
}

function RiskForm({ onAdd }) {
    const [v, setV] = useState({ name: "", value: "", notes: "" });
    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                if (!v.name) return;
                onAdd(v);
                setV({ name: "", value: "", notes: "" });
            }}
            className="mt-4 space-y-2"
        >
            <div className="flex gap-2">
                <input placeholder="Name (e.g. Max daily loss)" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} className="flex-1 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="risk-name" />
                <input placeholder="Value (e.g. 2%)" value={v.value} onChange={(e) => setV({ ...v, value: e.target.value })} className="w-32 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="risk-value" />
            </div>
            <div className="flex gap-2">
                <input placeholder="Notes" value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} className="flex-1 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                <button className="bg-[#D4AF37] text-[#0E0E10] px-3 rounded-md hover:bg-[#B3922E] text-sm" data-testid="risk-add"><Plus className="w-4 h-4" /></button>
            </div>
        </form>
    );
}

function SimpleAdd({ placeholder, onAdd }) {
    const [t, setT] = useState("");
    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                if (!t.trim()) return;
                onAdd(t.trim());
                setT("");
            }}
            className="mt-4 flex gap-2"
        >
            <input value={t} onChange={(e) => setT(e.target.value)} placeholder={placeholder} className="flex-1 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="simple-add-input" />
            <button className="bg-[#D4AF37] text-[#0E0E10] px-3 rounded-md hover:bg-[#B3922E] text-sm" data-testid="simple-add-btn"><Plus className="w-4 h-4" /></button>
        </form>
    );
}

function ChartObservationSection({ instruments }) {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ instrument: "", key_levels: "", scenarios: "", notes: "", link: "", screenshot_path: "" });
    const [uploading, setUploading] = useState(false);
    const load = async () => {
        const { data } = await api.get("/chart-observations");
        setList(data.sort((a, b) => b.observed_at.localeCompare(a.observed_at)));
    };
    useEffect(() => { load(); }, []);
    const upload = async (file) => {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm({ ...form, screenshot_path: data.id });
        } finally {
            setUploading(false);
        }
    };
    const save = async (e) => {
        e.preventDefault();
        if (!form.instrument) return;
        const { data } = await api.post("/chart-observations", {
            ...form,
            observed_at: new Date().toISOString(),
        });
        setList([data, ...list]);
        setForm({ instrument: "", key_levels: "", scenarios: "", notes: "", link: "", screenshot_path: "" });
        setOpen(false);
    };
    const del = async (id) => {
        await api.delete(`/chart-observations/${id}`);
        setList(list.filter((x) => x.id !== id));
    };

    return (
        <section className="twp-card p-6" data-testid="chart-obs-section">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-serif text-xl">Chart observations</h3>
                    <p className="text-sm text-zinc-500 mt-1">Notes, key levels and scenarios — never trade signals.</p>
                </div>
                <button onClick={() => setOpen(!open)} className="bg-[#D4AF37] text-[#0E0E10] px-3 py-2 rounded-md hover:bg-[#B3922E] text-sm inline-flex items-center gap-2" data-testid="obs-toggle">
                    <Plus className="w-4 h-4" /> New observation
                </button>
            </div>
            {open && (
                <form onSubmit={save} className="mt-5 grid md:grid-cols-2 gap-3" data-testid="obs-form">
                    <select value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" required data-testid="obs-instrument">
                        <option value="">Select instrument…</option>
                        {instruments.map((i) => <option key={i.id} value={i.symbol}>{i.symbol}</option>)}
                    </select>
                    <input placeholder="Link (TradingView, chart URL)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    <textarea placeholder="Key levels (e.g. 4550 support, 4620 resistance)" value={form.key_levels} onChange={(e) => setForm({ ...form, key_levels: e.target.value })} rows={2} className="md:col-span-2 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="obs-levels" />
                    <textarea placeholder="Scenarios ('If price holds X, watch for Y')" value={form.scenarios} onChange={(e) => setForm({ ...form, scenarios: e.target.value })} rows={2} className="md:col-span-2 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="obs-scenarios" />
                    <textarea placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="md:col-span-2 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} className="text-sm text-zinc-400" data-testid="obs-file" />
                    <div className="flex items-center gap-3">
                        {uploading && <span className="text-xs text-zinc-500">Uploading…</span>}
                        {form.screenshot_path && <span className="text-xs gold">Attached ✓</span>}
                    </div>
                    <button type="submit" className="bg-[#D4AF37] text-[#0E0E10] px-4 py-2 rounded-md hover:bg-[#B3922E] text-sm md:col-span-2" data-testid="obs-save">Save observation</button>
                </form>
            )}
            <div className="mt-5 space-y-3">
                {list.slice(0, 10).map((o) => (
                    <div key={o.id} className="border border-white/5 rounded-md p-4" data-testid={`obs-${o.id}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="font-mono text-[#D4AF37]">{o.instrument}</span>
                                <span className="text-xs text-zinc-500">{new Date(o.observed_at).toLocaleString()}</span>
                            </div>
                            <button onClick={() => del(o.id)} className="text-zinc-500 hover:text-[#B95050]"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        {o.key_levels && <div className="mt-2 text-sm"><span className="caption">Levels</span> <div className="text-zinc-300 mt-0.5">{o.key_levels}</div></div>}
                        {o.scenarios && <div className="mt-2 text-sm"><span className="caption">Scenarios</span> <div className="text-zinc-300 mt-0.5">{o.scenarios}</div></div>}
                        {o.notes && <div className="mt-2 text-sm text-zinc-400">{o.notes}</div>}
                        {o.link && <a href={o.link} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs text-[#D4AF37] hover:underline">Open link ↗</a>}
                    </div>
                ))}
                {list.length === 0 && <div className="text-sm text-zinc-500">No observations yet.</div>}
            </div>
        </section>
    );
}
